import AddIcon from '@mui/icons-material/Add';
import {
  Row, Col, Tag, Typography,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDebounce } from 'ahooks';
import StatusBadge from '../../../components/Badges/StatusBadge';
import AlumlyButton from '../../../components/AlumlyButton';
import AlumlyDataGrid from '../../../components/AlumlyDataGrid';
import { PermissionEnum } from '../../../enums/permission';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import usePermission from '../../../hooks/usePermission';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import { DateTimeUtils } from '../../../utils/dateTime';
import { PathBuilder } from '../../../utils/pathBuilder';
import FormFilterFeaturedContentList, { FilterFeaturedContentT } from './FormFilterFeaturedContentList';

const typeColors: Record<string, string> = {
  event: 'blue',
  news: 'magenta',
};

const FeaturedContentList: React.FC = () => {
  const navigate = useNavigate();
  const { checkPermission } = usePermission();

  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<FilterFeaturedContentT>();
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    rowCount: 0,
    loading: false,
  });

  const debouncedFilter = useDebounce(selectedFilter, { wait: 300 });
  const { service: getListService } = useServices(ServiceNameEnum.featuredContent.index);

  const getItems = async (): Promise<void> => {
    setLoading(true);
    const { data, error } = await getListService.get({});
    if (data) {
      setItems(data);
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
    }
    setLoading(false);
  };

  const filteredItems = useMemo(() => {
    let result = items;
    if (debouncedFilter?.searchQuery) {
      const q = debouncedFilter.searchQuery.toLowerCase();
      result = result.filter((item) =>
        (item.titleML?.en || '').toLowerCase().includes(q)
        || (item.titleML?.th || '').toLowerCase().includes(q));
    }
    if (debouncedFilter?.type) {
      result = result.filter((item) => item.type === debouncedFilter.type);
    }
    if (debouncedFilter?.status !== undefined && debouncedFilter?.status !== '') {
      const isPublished = debouncedFilter.status === 'true';
      result = result.filter((item) => item.isPublished === isPublished);
    }
    return result;
  }, [items, debouncedFilter]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      rowCount: filteredItems.length,
      page: 0,
    }));
  }, [filteredItems]);

  const onRowClick = (rowData: any): void => {
    const params = { id: rowData.id };
    const detailPath = PathBuilder.buildPath(RouteNameEnum.featuredContent.detail, { params });
    navigate(detailPath);
  };

  const columns: any[] = [
    {
      field: 'image',
      headerName: 'Image',
      width: 120,
      renderCell: (params: any) => (
        params.row.coverImageUrl
          ? <img src={params.row.coverImageUrl} alt="" style={{ width: 80, height: 60, borderRadius: 4, objectFit: 'cover' }} />
          : <div style={{ width: 80, height: 60, borderRadius: 4, backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography.Text type="secondary" style={{ fontSize: 12 }}>No image</Typography.Text></div>
      ),
    },
    {
      field: 'titleML',
      headerName: 'Title',
      width: 250,
      flex: 1,
      renderCell: (params: any) => (
        <Typography.Text style={{ fontSize: 14, wordWrap: 'break-word', whiteSpace: 'normal' }}>
          {params.row.titleML?.en || params.row.titleML?.th || '-'}
        </Typography.Text>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 100,
      renderCell: (params: any) => (
        <Tag
          color={typeColors[params.row.type] || 'default'}
        >
          {params.row.type}
        </Tag>
      ),
    },
    {
      field: 'displayOrder',
      headerName: 'Order',
      width: 80,
    },
    {
      field: 'startAt',
      headerName: 'Schedule',
      width: 200,
      renderCell: (params: any) => (
        <div style={{ display: 'flex-start', alignItems: 'center', flexDirection: 'column' }}>
          {params.row.startAt ? (
            <Typography.Text style={{ fontSize: 14 }}>
              {DateTimeUtils.dateTimeFormat(params.row.startAt)}
            </Typography.Text>
          ) : (
            <Typography.Text style={{ fontSize: 14 }}>Immediately</Typography.Text>
          )}
          {params.row.endAt && (
            <Typography.Text style={{ fontSize: 14 }}>
              {`- ${DateTimeUtils.dateTimeFormat(params.row.endAt)}`}
            </Typography.Text>
          )}
        </div>
      ),
    },
    {
      field: 'isPublished',
      headerName: 'Status',
      width: 120,
      renderCell: (params: any) => (
        <div>
          <StatusBadge status={params.row.isPublished} />
        </div>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 160,
      valueGetter: (params: any) => DateTimeUtils.dateTimeFormat(params.row.createdAt),
    },
  ];

  useEffect(() => {
    getItems();
  }, []);

  return (
    <div style={{ marginTop: 32 }}>
      <Row>
        {checkPermission([PermissionEnum.code.featuredContentCreate])
          && (
            <Col xs={24} style={{ textAlign: 'end' }}>
              <AlumlyButton
                icon={<AddIcon />}
                label="rxv"
                width={280}
                onClick={() => navigate(RouteNameEnum.featuredContent.create)}
              >
                Create new featured content
              </AlumlyButton>
            </Col>
          )}
      </Row>
      <FormFilterFeaturedContentList
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
      />
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 32,
            marginBottom: 24,
          }}
        >
          <div style={{ display: 'flex' }}>
            <Typography.Text style={{ marginRight: 8 }}>Total</Typography.Text>
            <Typography.Text strong>{`${filteredItems.length} Items`}</Typography.Text>
          </div>
        </div>
      </div>
      <AlumlyDataGrid
        rowHeight={80}
        height={700}
        rows={filteredItems}
        columns={columns}
        onRowClick={
          checkPermission([PermissionEnum.code.featuredContentViewList])
            ? onRowClick
            : undefined
        }
        {...pagination}
        loading={loading}
        onPageChange={(page: number) => setPagination((prev) => ({ ...prev, page }))}
      />
    </div>
  );
};

export default FeaturedContentList;
